import { AgentExecutionLiveTail } from '@/components/AgentModals';
import { CenterSpin } from '@/components/CenterSpin';
import { useAgentExecutionById } from '@/queries/agent-executions';
import { useAgentById } from '@/queries/agents';
import { formatRelativeTime } from '@/utils/format';
import ArrowLeftOutlined from '@ant-design/icons/ArrowLeftOutlined';
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import FullscreenOutlined from '@ant-design/icons/FullscreenOutlined';
import PauseCircleOutlined from '@ant-design/icons/PauseCircleOutlined';
import PlayCircleOutlined from '@ant-design/icons/PlayCircleOutlined';
import ReloadOutlined from '@ant-design/icons/ReloadOutlined';
import StopOutlined from '@ant-design/icons/StopOutlined';
import Alert from 'antd/es/alert';
import Button from 'antd/es/button';
import Card from 'antd/es/card';
import Col from 'antd/es/col';
import Empty from 'antd/es/empty';
import message from 'antd/es/message';
import Row from 'antd/es/row';
import Space from 'antd/es/space';
import Statistic from 'antd/es/statistic';
import Tabs from 'antd/es/tabs';
import Tag from 'antd/es/tag';
import Tooltip from 'antd/es/tooltip';
import Typography from 'antd/es/typography';
import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';

import { type AgentExecution } from '@mai/types';

const { Title, Text, Paragraph } = Typography;

const PageContainer = styled.div`
  ${({ theme }) => `
  padding: ${theme.paddingLG}px;
  max-width: ${theme.screenXLMax * 1.14}px;
  margin: 0 auto;
`}
`;

const HeaderContainer = styled.div`
  ${({ theme }) => `
  margin-bottom: ${theme.marginLG}px;
`}
`;

const BackButton = styled(Button)`
  ${({ theme }) => `
  margin-bottom: ${theme.margin}px;
`}
`;

const TitleContainer = styled.div`
  ${({ theme }) => `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.margin}px;
`}
`;

const StatusTag = styled(Tag)<{ $status: string }>`
  ${(props) => {
    switch (props.$status) {
      case 'running':
        return 'color: #1890ff; background: #e6f7ff; border-color: #91d5ff;';
      case 'completed':
        return 'color: #52c41a; background: #f6ffed; border-color: #b7eb8f;';
      case 'failed':
        return 'color: #ff4d4f; background: #fff2f0; border-color: #ffccc7;';
      case 'paused':
        return 'color: #fa8c16; background: #fff7e6; border-color: #ffd591;';
      default:
        return '';
    }
  }}
`;

const JsonPreview = styled.pre`
  ${({ theme }) => `
  background: #f5f5f5;
  padding: ${theme.paddingXS}px;
  border-radius: ${theme.borderRadius * 2.6}px;
  overflow: auto;
  max-height: ${theme.sizeXXL / 4}px;
  font-size: 12px;
`}
`;

function formatDuration(startedAt: Date | null, endedAt: Date | null): string {
  if (!startedAt) return '-';
  const end = endedAt ? new Date(endedAt) : new Date();
  const start = new Date(startedAt);
  const duration = end.getTime() - start.getTime();
  const seconds = Math.floor(duration / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
}

function getExecutionEndTime(execution: AgentExecution): Date | null {
  return (
    execution.completedAt || execution.failedAt || execution.cancelledAt || null
  );
}

export const AgentExecutionDetailPage: React.FC = () => {
  const { teamId, agentId, executionId } = useParams<{
    teamId: string;
    agentId: string;
    executionId: string;
  }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('live-tail');
  const theme = useTheme();
  const {
    data: execution,
    isLoading: isLoadingExecution,
    error: executionError,
  } = useAgentExecutionById(executionId!);

  const { data: agent, isLoading: isLoadingAgent } = useAgentById(agentId!);

  if (isLoadingExecution || isLoadingAgent) {
    return <CenterSpin />;
  }

  if (executionError || !execution) {
    return (
      <PageContainer>
        <Alert
          message="Execution not found"
          description="The requested execution could not be found."
          type="error"
          showIcon
          action={
            <Button size="small" onClick={() => navigate(-1)}>
              Go Back
            </Button>
          }
        />
      </PageContainer>
    );
  }

  const isRunning = execution.status === 'running';
  const isPaused = execution.status === 'paused';
  const isFinished = ['completed', 'failed', 'cancelled'].includes(
    execution.status,
  );

  const handlePauseResume = async () => {
    // TODO: Implement pause/resume functionality
    await message.info(
      isPaused
        ? 'Resume functionality coming soon'
        : 'Pause functionality coming soon',
    );
  };

  const handleStop = async () => {
    // TODO: Implement stop functionality
    await message.info('Stop functionality coming soon');
  };

  const handleRerun = async () => {
    // TODO: Implement rerun functionality
    await message.info('Rerun functionality coming soon');
  };

  const handleDownloadResults = async () => {
    // TODO: Implement download functionality
    await message.info('Download functionality coming soon');
  };

  return (
    <PageContainer>
      <HeaderContainer>
        <Link to={`/team/${teamId}/agents/${agentId}`}>
          <BackButton type="text" icon={<ArrowLeftOutlined />}>
            Back to {agent?.name || 'Agent'}
          </BackButton>
        </Link>

        <TitleContainer>
          <Space align="center" size="large">
            <Title level={2} style={{ margin: 0 }}>
              Execution Details
            </Title>
            <StatusTag $status={execution.status}>
              {execution.status.toUpperCase()}
            </StatusTag>
          </Space>

          <Space>
            {isRunning && (
              <>
                <Tooltip
                  title={isPaused ? 'Resume execution' : 'Pause execution'}
                >
                  <Button
                    icon={
                      isPaused ? (
                        <PlayCircleOutlined />
                      ) : (
                        <PauseCircleOutlined />
                      )
                    }
                    onClick={handlePauseResume}
                  >
                    {isPaused ? 'Resume' : 'Pause'}
                  </Button>
                </Tooltip>
                <Tooltip title="Stop execution">
                  <Button icon={<StopOutlined />} danger onClick={handleStop}>
                    Stop
                  </Button>
                </Tooltip>
              </>
            )}
            {isFinished && (
              <>
                <Button icon={<ReloadOutlined />} onClick={handleRerun}>
                  Run Again
                </Button>
                {execution.agentOutput && (
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadResults}
                  >
                    Download Results
                  </Button>
                )}
              </>
            )}
          </Space>
        </TitleContainer>
      </HeaderContainer>

      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Started"
                  value={
                    execution.startedAt
                      ? formatRelativeTime(execution.startedAt)
                      : 'Not started'
                  }
                  prefix={<ClockCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Duration"
                  value={formatDuration(
                    execution.startedAt,
                    getExecutionEndTime(execution),
                  )}
                  prefix={<ClockCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Current Step"
                  value={execution.currentStep || 0}
                  suffix={`/ ${execution.maxActions || '∞'}`}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Version"
                  value={
                    execution.agentConfigurationVersionId
                      .split('_')
                      .pop()
                      ?.substring(0, 8) || '-'
                  }
                  prefix="#"
                />
              </Card>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Card>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={[
                {
                  key: 'live-tail',
                  label: (
                    <span>
                      <FullscreenOutlined /> Live Progress
                    </span>
                  ),
                  children: (
                    <AgentExecutionLiveTail
                      agentExecutionId={execution.agentExecutionId}
                    />
                  ),
                },
                {
                  key: 'input',
                  label: 'Input',
                  children: (
                    <div>
                      <Title level={5}>Input Summary</Title>
                      <Paragraph>{execution.agentInputSummary}</Paragraph>
                      <Title
                        level={5}
                        style={{ marginTop: `${theme.marginLG}px` }}
                      >
                        Raw Input
                      </Title>
                      <JsonPreview>
                        {JSON.stringify(execution.agentInput, null, 2)}
                      </JsonPreview>
                    </div>
                  ),
                },
                {
                  key: 'output',
                  label: 'Output',
                  disabled: !execution.agentOutput,
                  children: execution.agentOutput ? (
                    <div>
                      <Title level={5}>Output Summary</Title>
                      <Paragraph>
                        {execution.agentOutputSummary || 'No summary available'}
                      </Paragraph>
                      <Title
                        level={5}
                        style={{ marginTop: `${theme.marginLG}px` }}
                      >
                        Raw Output
                      </Title>
                      <JsonPreview>
                        {JSON.stringify(execution.agentOutput, null, 2)}
                      </JsonPreview>
                    </div>
                  ) : (
                    <Empty description="No output available yet" />
                  ),
                },
                {
                  key: 'configuration',
                  label: 'Configuration',
                  children: (
                    <div>
                      <Space
                        direction="vertical"
                        size="large"
                        style={{ width: '100%' }}
                      >
                        <div>
                          <Title level={5}>Execution Limits</Title>
                          <Space direction="vertical">
                            <Text>
                              Max Actions: {execution.maxActions || 'Unlimited'}
                            </Text>
                            <Text>
                              Planning Interval:{' '}
                              {execution.planningInterval || 'Default'}
                            </Text>
                          </Space>
                        </div>
                        <div>
                          <Title level={5}>Configuration Version</Title>
                          <Text code>
                            {execution.agentConfigurationVersionId}
                          </Text>
                        </div>
                      </Space>
                    </div>
                  ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </PageContainer>
  );
};
